import { useState, useEffect } from "react";
import { FilterEditForm } from "common/filter/form";
import {
  getFilteredDefaults,
  isFilterValid,
} from "common/filter/form/functions";
import type { FormValidation } from "common/form/types";
import { Context } from "common/types/context";
import { defaultFilter as defaultValue, Filter } from "common/types/filters";
import { DeleteButton } from "common/ui/buttons";
import { Modal } from "common/widgets/modal";
import { ValueProps } from "common/with-value-for";
import { getTitle, isOkLabel } from "x/records/list/filter/modal/functions";
import { getFiltersPermissions } from "x/records/list/functions";
import { SaveFilterState } from "x/records/list/types";

interface PropTypes extends ValueProps<Filter> {
  saveFilterState: SaveFilterState;
  context: Context;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  disableGranularShare?: boolean;
}

export const SaveFilterModal = ({
  saveFilterState,
  context,
  onSave,
  onCancel,
  onDelete,
  disableGranularShare,
  value = defaultValue,
  onChange,
}: PropTypes) => {
  const [formValidation, setFormValidation] = useState<FormValidation>();
  const [shouldTriggerSave, setShouldTriggerSave] = useState(false);

  useEffect(() => {
    if (shouldTriggerSave) {
      onSave?.();
      setShouldTriggerSave(false);
    }
  }, [shouldTriggerSave, onSave]);

  const onSaveModal = () => {
    onChange({
      ...value,
      defaults: getFilteredDefaults(value),
    });

    setShouldTriggerSave(true);
  };

  const isValid = () => isFilterValid(value, formValidation);

  const { canDelete } = getFiltersPermissions(context, ["Delete"]);
  const footerButtons =
    saveFilterState === "edit" && canDelete ? (
      <DeleteButton className="x-float-left qa-btn-delete" onClick={onDelete} />
    ) : undefined;

  return (
    <Modal
      content={
        <FilterEditForm
          context={context}
          formValidation={formValidation}
          onFormValidationChange={setFormValidation}
          onSave={isValid() ? onSave : undefined}
          disableDefaultsChange={true}
          disableGranularShare={disableGranularShare}
          filterDefaultsOnChange={false}
          value={value}
          onChange={onChange}
        />
      }
      title={getTitle(saveFilterState)}
      okLabel={isOkLabel(saveFilterState)}
      onOk={isValid() ? onSaveModal : undefined}
      onCancel={onCancel}
      footerButtons={footerButtons}
    />
  );
};
